import { useEffect, useMemo, useRef, useState } from 'react'

import { useRouter } from 'next/router'

import { useNewSurvey, useNewSurveySubmissions } from '@context/new-survey/useNewSurvey'
import useCart from '@context/useCartContext'

import GetStartedPlansConfig from '@config/get-started-plans-config'
import SubscriptionBoxData from '@data/subscription-box.json'

import useCustomer from '@lib/hooks/useCustomer'
import useGetCheckoutUrl from '@lib/hooks/useGetCheckoutUrl'
import useAddItem from '@lib/cart/use-add-item'
import type {
	GetStartedPlansConfig as GetStartedPlansConfigType,
	SubscriptionBoxSellingPlanData
} from '@lib/types'
import setCookie from '@lib/utils/set-cookies'
import { upgradeClaritySession } from '@lib/analytics/clarity'
import { trackGetStartedEvent } from '@lib/analytics/get-started-tracking'
import type { GetStartedOffer } from '@lib/types/get-started-offer'
import { FALLBACK_GIFT_PRODUCTS } from '@lib/types/get-started-offer'
import type { GetStartedPageContent } from '@lib/types/get-started-page'
import { FALLBACK_PAGE_CONTENT } from '@lib/types/get-started-page'

import Logo from '@components/global/Logo'
import ScrollOverflowIndicator from '@components/scroll-indicators/ScrollOverflowIndicator'
import Spinner from '@components/Spinner'

import type { AnnouncementConfig } from '@lib/new-survey/get-new-survey'

import MobilePlanSelection from './MobilePlanSelection'

// Annual plan is the first entry in config (has a tag/badge and bonus)
const ANNUAL_SELLING_PLAN_ID = (GetStartedPlansConfig as GetStartedPlansConfigType).plans[0]?.sellingPlanId

function resolveOfferSubheadline(offer: GetStartedOffer): string | null {
	if (offer.planSubheadline) return offer.planSubheadline
	if (offer.type === 'free_gift') {
		return `Pick Annual and we'll add a free ${offer.giftValueText ?? '$138'} gift to your first order`
	}
	if (offer.couponCode) {
		return `Pick Annual — save $36 a year, then use ${offer.couponCode} for 20% off your first box`
	}
	return null
}

function resolveAnnualCallout(offer: GetStartedOffer): string | null {
	if (offer.type === 'free_gift') return null
	if (offer.annualCallout) return offer.annualCallout
	if (offer.couponCode) return '20% off your first box'
	return null
}

function resolveAnnualTagSuffix(offer: GetStartedOffer): string | null {
	if (offer.type !== 'discount') return null
	return offer.annualTagSuffix ?? '20% off 1st box'
}

function resolveAnnualGiftOffer(offer: GetStartedOffer) {
	if (offer.type !== 'free_gift') return null
	return {
		products: offer.giftProducts?.length ? offer.giftProducts : FALLBACK_GIFT_PRODUCTS,
		valueText: offer.giftValueText ?? '$138',
		cardLabel: offer.giftCardLabel,
		cardSubtext: offer.giftCardSubtext,
		cardCta: offer.giftCardCta,
		modalHeading: offer.giftModalHeading,
		modalSubtext: offer.giftModalSubtext,
		modalDisclaimer: offer.giftModalDisclaimer
	}
}

type GetStartedPlanSelectionProps = {
	onComplete?: () => void
	announcement?: AnnouncementConfig | null
	offer?: GetStartedOffer | null
	pageContent?: GetStartedPageContent
}

export default function GetStartedPlanSelection({
	onComplete,
	announcement,
	offer,
	pageContent: pageContentProp
}: GetStartedPlanSelectionProps) {
	const router = useRouter()
	const pageContent = pageContentProp ?? FALLBACK_PAGE_CONTENT
	const { customer } = useCustomer()
	const surveySubmissions = useNewSurveySubmissions()
	const { addSurveyAnswerToCartAttributes, addExperimentVariantToCart, items } = useCart()
	const addItem = useAddItem()
	const { surveyQuestions } = useNewSurvey() || {}
	const config = GetStartedPlansConfig as GetStartedPlansConfigType
	const checkoutEnabled = config.checkoutStep.enabled
	const checkoutUrl = useGetCheckoutUrl()
	const scrollRef = useRef<HTMLDivElement>(null)
	const selectedPlanTypeRef = useRef<'annual' | 'monthly'>('monthly')
	const [planSelected, setPlanSelected] = useState(false)
	const [submitting, setSubmitting] = useState(false)
	const [redirecting, setRedirecting] = useState(false)

	const questionCount = surveyQuestions?.length || 0
	const currentStep = questionCount + 2 // auth is +1, plan is +2
	const totalSteps = questionCount + 2 + (checkoutEnabled ? 1 : 0)

	// Resolve plans from config: order and tags come from get-started-plans.json
	const plansToDisplay = useMemo(() => {
		const allPlans = SubscriptionBoxData as SubscriptionBoxSellingPlanData[]

		return config.plans
			.map((configEntry) => {
				const plan = allPlans.find((p) => p.sellingPlanId === configEntry.sellingPlanId)
				if (!plan) {
					console.warn(
						`[get-started-plans] Plan not found for sellingPlanId: ${configEntry.sellingPlanId}`
					)
					return null
				}
				return {
					...plan,
					tag: configEntry.tag ?? null,
					benefits: configEntry.benefits,
					bonus: configEntry.bonus ?? null
				}
			})
			.filter(Boolean) as (SubscriptionBoxSellingPlanData & {
			tag: string | null
			benefits?: string[]
			bonus?: { text: string; subtext?: string } | null
		})[]
	}, [])

	// After plan is added to cart AND items are present, write survey data to cart attributes
	useEffect(() => {
		if (!planSelected || !items || items.length === 0 || submitting) return

		const writeSurveyToCart = async () => {
			setSubmitting(true)
			try {
				const surveyAnswers = surveySubmissions.map(({ questionId, response }) => ({
					question_id: questionId,
					answer: response.toString()
				}))

				await addSurveyAnswerToCartAttributes(surveyAnswers)
				setCookie('bbSurveySubmitted', 'true', 48)

				trackGetStartedEvent('checkout_initiated', {
					plan_type: selectedPlanTypeRef.current,
					offer_type: offer?.type ?? 'none'
				})

				if (checkoutEnabled && onComplete) {
					onComplete()
				} else {
					setRedirecting(true)
				}
			} catch (err) {
				console.error('Error writing survey data to cart:', err)
				setSubmitting(false)
			}
		}

		writeSurveyToCart()
	}, [planSelected, items])

	// Redirect to checkout after survey data is saved (when checkout step is disabled)
	useEffect(() => {
		if (redirecting && checkoutUrl && checkoutUrl !== '/') {
			window.open(checkoutUrl, '_self')
		}
	}, [redirecting, checkoutUrl])

	const handleAddToCart = async (plan: { sellingPlanId: string; tag: string | null; name?: string }) => {
		const isAnnualPlan = plan.sellingPlanId === ANNUAL_SELLING_PLAN_ID
		const planType: 'annual' | 'monthly' = isAnnualPlan ? 'annual' : 'monthly'
		selectedPlanTypeRef.current = planType

		trackGetStartedEvent('plan_selected', {
			plan_id: plan.sellingPlanId,
			plan_type: planType
		})
		upgradeClaritySession('get_started_plan_selected')

		// Fire server-side to avoid CORS on a.klaviyo.com/client/events/
		if (customer?.email) {
			fetch('/api/klaviyo/track-event', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					event: 'Selected Plan',
					email: customer.email,
					properties: {
						source: 'get-started funnel',
						plan_id: plan.sellingPlanId,
						plan_type: planType,
						plan_name: plan.name,
					},
				}),
			}).catch((err) => console.error('[klaviyo] Selected Plan track failed:', err))
		}

		// Record the active offer type against the order for attribution.
		if (offer?.type) {
			addExperimentVariantToCart('exp_get_started_offer', offer.type)
		}

		// For free_gift offers: add the gift product alongside the annual subscription.
		// Await so the cartLinesAdd mutation completes before setPlanSelected triggers the
		// checkout redirect — otherwise the gift races against the navigation and loses.
		if (offer?.type === 'free_gift' && offer.giftVariantId && isAnnualPlan) {
			await addItem({
				variantId: offer.giftVariantId,
				quantity: 1,
				attributes: [{ key: 'source', value: 'get_started_gift' }]
			}).catch((err) => {
				console.error('[get-started] Failed to add gift product to cart:', err)
			})
		}

		setPlanSelected(true)
	}

	if (submitting) {
		return (
			<div className="flex flex-col items-center justify-center py-20">
				<Spinner />
				<p className="mt-4 text-gray-500">
					{redirecting ? 'Redirecting to checkout...' : 'Saving your preferences...'}
				</p>
			</div>
		)
	}

	return (
		<div className="flex flex-col h-[100dvh] bg-white md:bg-transparent">
			{/* Fixed top: Logo + announcement + progress */}
			<div className="flex-shrink-0 bg-white">
				<div className="container">
					{/* Logo bar */}
					<div className="flex justify-center py-3 md:py-5">
						<button
							type="button"
							aria-label="Birchbox Home"
							className="cursor-pointer"
							onClick={() => {
								if (typeof window !== 'undefined' && window.history.length > 1) {
									router.back()
								} else {
									router.push('/')
								}
							}}
						>
							<Logo className="w-28 md:w-32 fill-bbx-style" />
						</button>
					</div>
				</div>
			</div>

			{/* Plan selection content */}
			<div className="relative flex-1 min-h-0 overflow-hidden">
				<div ref={scrollRef} className="h-full overflow-y-auto bg-white container">
					<div className="flex flex-col items-center w-full pb-6 max-w-4xl mx-auto px-4 md:px-12 md:min-h-full md:justify-center">
						{/* Progress row — scrolls with content */}
						<div className="flex flex-wrap items-center gap-3 pt-3 pb-4 md:pt-6 w-full">
							<span className="text-sm text-bbx-style whitespace-nowrap">
								{currentStep} of {totalSteps}
							</span>
							<div className="relative flex-1 h-4 overflow-hidden bg-bbx-glimmer-light border-[3px] border-bbx-glimmer-light">
								<div
									className="absolute inset-y-0 left-0 h-2.5 transition-all duration-[800ms] ease-in-out bg-bbx-glimmer"
									style={{ width: `${(currentStep / totalSteps) * 100}%` }}
								/>
							</div>
						</div>
						<MobilePlanSelection
							plans={plansToDisplay}
							onAddToCart={handleAddToCart}
							pageContent={pageContent}
							offerSubheadline={offer ? resolveOfferSubheadline(offer) : null}
							annualOfferCallout={offer ? resolveAnnualCallout(offer) : null}
							annualCouponCode={offer?.type === 'discount' ? (offer.couponCode ?? null) : null}
							annualTagSuffix={offer ? resolveAnnualTagSuffix(offer) : null}
							annualGiftOffer={offer ? resolveAnnualGiftOffer(offer) : null}
						/>
					</div>
				</div>
				<ScrollOverflowIndicator scrollRef={scrollRef} showGradientFade showBouncingChevron />
			</div>
		</div>
	)
}
